import { Body2, Box, Container, Display3 } from '@sainsburys-tech/fable'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { useApiClient } from '../../../common/AppContext/appContext'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import ProgressLoading from '../../../common/components/ProgressLoading'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { IIngredient } from '../../../common/types/IIngredient'
import { getUserStore } from '../../../utils/localStore'
import {
  getIngredients,
  getIngredientSubCategories,
} from '../../api/ingredientsApi'
import { ISubCategory } from '../../types/ISubCategory'
import IngredientTable from './IngredientTable'

const IngredientView = () => {
  const { dispatch } = useContext(Context)
  const apiClient = useApiClient()

  const store = useMemo(() => getUserStore(), [])

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [subCategories, setSubCategories] = useState<ISubCategory[]>([])
  const [ingredients, setIngredients] = useState<IIngredient[]>()

  const { handleError, resetError } = useErrorHandler(setErrorMessage)

  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.Bakery,
      handleError,
    )

  const isLoading = initLoading || isDataLoading

  useEffect(() => {
    const loadIngredientsData = async () => {
      setIsDataLoading(true)
      resetError() // Clear previous errors

      try {
        const [ingredientsData, subCategoriesData] = await Promise.all([
          getIngredients(apiClient, store.storeId),
          getIngredientSubCategories(apiClient, store.storeId),
        ])

        setIngredients(ingredientsData)
        setSubCategories(subCategoriesData)
      } catch (e) {
        handleError(ErrorMessage.IngredientsView.FailedToLoad, e as Error)
      } finally {
        setIsDataLoading(false)
      }
    }

    loadIngredientsData()
  }, [apiClient, store.storeId, handleError, resetError])

  const handleIngredientChecked = (ingredientId: number) => {
    if (!ingredients) {
      return
    }

    const clone = ingredients.map((x) => ({ ...x }))
    const toBeUpdated = clone.find((x) => x.ingredientId === ingredientId)!
    toBeUpdated.checked = !toBeUpdated.checked
    setIngredients(clone)
  }

  const handleSelectAll = (subCategoryId: number) => {
    if (!ingredients) {
      return
    }

    const clone = ingredients.map((x) => ({ ...x }))
    clone.forEach((x) => {
      if (x.subCategoryId === subCategoryId) {
        x.checked = true
      }
    })
    setIngredients(clone)
  }

  const handleDeselectAll = () => {
    if (!ingredients) {
      return
    }

    const clone = ingredients.map((x) => ({ ...x }))
    clone.forEach((x) => (x.checked = false))
    setIngredients(clone)
  }

  const renderIngredientTable = () =>
    subCategories.length > 0 &&
    ingredients &&
    ingredients.length > 0 && (
      <IngredientTable
        handleIngredientChecked={handleIngredientChecked}
        handleDeselectAll={handleDeselectAll}
        handleSelectAll={handleSelectAll}
        ingredients={ingredients}
        initialTab={subCategories[0].subCategoryId}
        subCategories={subCategories}
      />
    )

  return (
    <>
      <div className='c-common-hide-in-print'>
        {isLoading && <ProgressLoading message='Loading...' />}

        {!isLoading && (
          <Box backgroundColor='primary' padding='none'>
            <Header
              title={headerPlannerTitles.bakery}
              navItems={headerNavigationItems}
              activeMenuType={Menu.Ingredient}
            />
            <Container className='c-common-main-view-content'>
              <div>
                <Display3 style={{ paddingBottom: '16px' }}>
                  Ordering for USEORD
                </Display3>
                <div>
                  <Body2
                    style={{ fontWeight: 'normal', paddingBottom: '16px' }}
                  >
                    View the minimum amount of cases you'll need for each SKU
                    below, to produce your forecast. Select the required SKUs to
                    print a list, then complete your orders on USEORD. Any
                    editing of forecasts will not be reflected here until next
                    day.
                  </Body2>
                </div>
                {renderIngredientTable()}
              </div>

              {errorMessage && <ErrorRefresh message={errorMessage} />}
            </Container>
          </Box>
        )}
      </div>

      <div className='c-common-only-visible-in-print'>
        {renderIngredientTable()}
      </div>
    </>
  )
}

export default IngredientView
